/**
 * Global fetch tracker — monkey-patches window.fetch to observe ALL
 * backend requests and report failures / slow responses to PostHog.
 *
 * This module is a pure observer: it never modifies request/response
 * behaviour and wraps every tracking call in try/catch so it can never
 * break the app.
 *
 * Import once at app boot (main.jsx) — no further wiring needed.
 *
 * Events sent (via trackEvent → prefixed with "Dashboard:"):
 *   • RequestToBackend:Failed   — HTTP 4xx/5xx or network error
 *   • RequestToBackend:Slow     — request exceeded a duration tier
 *
 * Only requests whose URL matches a known backend domain are tracked
 * (codedesign.ai, codedesign.app, cdsn.me, cdsn.app) — excluding any
 * analytics-proxy calls so we don't track ourselves.
 */

import { trackEvent } from '@/lib/analytics'

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Domains we consider "backend" */
const BACKEND_DOMAIN_PATTERNS = [
  'codedesign.ai',
  'codedesign.app',
  'cdsn.me',
  'cdsn.app',
]

/** Domains we must exclude (our own analytics proxies) */
const EXCLUDED_PATTERNS = ['analytics-proxy', 'analytics.codedesign.app']

/**
 * PostHog internal paths to exclude (may be served via reverse proxy on the same domain), plus our own
 * Journeys funnel endpoint (/funnel/event: fire-and-forget analytics must never feed back into PostHog).
 */
const EXCLUDED_PATHS = ['/api/send', '/api/capture', '/api/decide', '/api/feature_flag', '/batch', '/decide', '/e/', '/funnel/event']

/**
 * Slow-request tiers (label + threshold in ms, ordered ascending).
 * The 3s tier was dropped to cut PostHog volume: most "slow" requests sit in
 * the 3-10s band and aren't actionable. Only genuinely-slow requests (>=10s)
 * are reported now.
 */
const SLOW_TIERS = [
  { label: 'slow_10s', threshold: 10_000 },
  { label: 'slow_30s', threshold: 30_000 },
  { label: 'slow_60s', threshold: 60_000 },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isBackendUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin)
    const hostname = parsed.hostname
    const pathname = parsed.pathname
    if (EXCLUDED_PATTERNS.some((p) => hostname.includes(p))) return false
    if (EXCLUDED_PATHS.some((p) => pathname.startsWith(p))) return false
    return BACKEND_DOMAIN_PATTERNS.some((d) => hostname.endsWith(d))
  } catch {
    return false
  }
}

function extractEndpoint(url) {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.pathname
  } catch {
    return String(url)
  }
}

function getSlowTier(durationMs) {
  let matched = null
  for (const tier of SLOW_TIERS) {
    if (durationMs >= tier.threshold) {
      matched = tier.label
    }
  }
  return matched
}

function extractMethod(init) {
  return (init?.method || 'GET').toUpperCase()
}

/**
 * Safely read a short error message from a cloned response.
 * Uses response.clone() so the original body stream is never consumed.
 */
async function extractErrorMessage(response) {
  try {
    const cloned = response.clone()
    const contentType = cloned.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const json = await cloned.json()
      return json?.message || json?.error || json?.errorMessage || JSON.stringify(json).slice(0, 200)
    }
    const text = await cloned.text()
    return text.slice(0, 200)
  } catch {
    return undefined
  }
}

// ---------------------------------------------------------------------------
// The patch
// ---------------------------------------------------------------------------

const _originalFetch = window.fetch

window.fetch = async function trackedFetch(input, init) {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof Request
        ? input.url
        : String(input)

  // Passthrough for non-backend requests
  if (!isBackendUrl(url)) {
    return _originalFetch.apply(this, arguments)
  }

  const method = extractMethod(init || (input instanceof Request ? input : undefined))
  const endpoint = extractEndpoint(url)
  const start = performance.now()

  let response
  try {
    response = await _originalFetch.apply(this, arguments)
  } catch (networkError) {
    // Network / CORS / DNS failure — the original error still propagates
    const durationMs = Math.round(performance.now() - start)
    try {
      trackEvent('RequestToBackend:Failed', {
        url,
        endpoint,
        method,
        status: 0,
        duration_ms: durationMs,
        error_message: networkError?.message || 'Network error',
        error_type: 'network_error',
      })
    } catch {
      /* observer — never crash */
    }
    throw networkError // re-throw original error untouched
  }

  const durationMs = Math.round(performance.now() - start)

  // Track HTTP-level failures (4xx / 5xx), but NOT 401 to reduce noise
  // (401s trigger forceLogout and are expected during auth transitions)
  if (!response.ok && response.status !== 401) {
    try {
      const errorMessage = await extractErrorMessage(response)
      trackEvent('RequestToBackend:Failed', {
        url,
        endpoint,
        method,
        status: response.status,
        duration_ms: durationMs,
        error_message: errorMessage,
        error_type: 'http_error',
      })
    } catch {
      /* observer — never crash */
    }
  }

  // Track slow requests (successful or failed)
  const tier = getSlowTier(durationMs)
  if (tier) {
    try {
      trackEvent('RequestToBackend:Slow', {
        url,
        endpoint,
        method,
        status: response.status,
        duration_ms: durationMs,
        tier,
      })
    } catch {
      /* observer — never crash */
    }
  }

  return response // always return the original response untouched
}
